"use client";
import { createContext, ReactNode, useContext } from "react";

/**
 * Horizontal space (px) that page chrome — currently just the nav rail — takes
 * away from the width available to page content. Defaults to 0, which is correct
 * for every layout that doesn't render a rail.
 */
const ContentInsetContext = createContext(0);

/**
 * How much narrower than the window the page's content area is. 0 means content
 * gets the whole window: there's no rail (mobile drawer, live share, exports, the
 * layouts that hide navigation), or the subtree spans the window itself.
 *
 * Read this instead of picking between SIDEBAR_WIDTH/SIDEBAR_COLLAPSED_WIDTH
 * yourself — the layout that owns the rail is the only thing that knows whether
 * it's actually on screen.
 */
export const useContentInset = () => useContext(ContentInsetContext);

/**
 * Publishes the inset to the theme (whose breakpoints are offset by it) and to
 * the components that lay out around the rail. Must sit *above* the MUI
 * ThemeProvider — in practice that means wrapping the layout's page body.
 *
 * To take a subtree back out to the full window, use `ViewportBreakpoints`, which
 * resets both this and the theme.
 */
export const ContentInsetProvider = ({ inset, children }: { inset: number; children: ReactNode }) => (
    <ContentInsetContext.Provider value={inset}>{children}</ContentInsetContext.Provider>
);
